import { useEffect, useMemo, useState, FC } from "react";
import { UserPlus } from "lucide-react";

import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";
import { NamedTextInput } from "../../ui/layout/molecules/NamedTextInput";
import { SlideOverWrapper } from "../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import {
  SLIDEOVER_EDIT_ICON_ICON_STYLES,
  SLIDEOVER_EDIT_ICON_WRAPPER_STYLES,
} from "../../ui/layout/organisms/SlideOver/styles";
import { Closeable } from "../../ui/layout/organisms/SlideOver/SlideOverWrapper/types";
import { handleUser } from "../handleUser";

const ALREADY_MEMBER_MESSAGE =
  "A user with this email address is already a member of this company.";
const EMAILS_DONT_MATCH_MESSAGE = "Emails don't match.";
const INVALID_EMAIL_MESSAGE = "Please enter a valid email address.";
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type MemberByEmailSlideOverProps = {
  title: string;
  submitTitle: string;
  currentCompanyId: string;
  existingMembers: { email: string }[];
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const MemberByEmailSlideOver: FC<
  React.PropsWithChildren<MemberByEmailSlideOverProps & Closeable>
> = ({
  open,
  title,
  submitTitle,
  currentCompanyId,
  existingMembers,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setConfirmEmail("");
  }, [open]);

  const emailsMatch = !!email && email === confirmEmail;
  const emailsMismatch = !!email && !!confirmEmail && email !== confirmEmail;

  const isAlreadyMember = useMemo(() => {
    if (!email) return false;
    const normalizedEmail = normalizeEmail(email);
    return existingMembers.some(
      (member) => normalizeEmail(member.email) === normalizedEmail,
    );
  }, [email, existingMembers]);

  const errorMessage = useMemo(() => {
    if (emailsMismatch) return EMAILS_DONT_MATCH_MESSAGE;
    if (emailsMatch && !EMAIL_FORMAT.test(normalizeEmail(email)))
      return INVALID_EMAIL_MESSAGE;
    if (isAlreadyMember) return ALREADY_MEMBER_MESSAGE;
    return undefined;
  }, [emailsMismatch, emailsMatch, email, isAlreadyMember]);

  const handlerParams = useRequestHandlers();
  const { handleInviteUser } = handleUser(handlerParams);

  const handleAccept = () =>
    handleInviteUser(normalizeEmail(email), currentCompanyId);

  return (
    <SlideOverWrapper
      open={open}
      buttonProps={{
        onClick: handleAccept,
        title: submitTitle,
        disabled: !emailsMatch || !!errorMessage,
      }}
      onClose={onClose}
      title={title}
      message={errorMessage}
    >
      <div className={SLIDEOVER_EDIT_ICON_WRAPPER_STYLES}>
        <UserPlus className={SLIDEOVER_EDIT_ICON_ICON_STYLES} />
      </div>

      <NamedTextInput
        title="Email"
        type="email"
        required
        value={email}
        onChange={(target) => setEmail(target.value)}
      />

      <NamedTextInput
        title="Confirm email"
        type="email"
        required
        value={confirmEmail}
        onChange={(target) => setConfirmEmail(target.value)}
      />
    </SlideOverWrapper>
  );
};
